import { useFeatureIsOn, useFeatureValue } from "@growthbook/growthbook-react";
import classNames from "classnames";
import { CollapsibleContainerRef } from "mt-components/CollapsibleContainer/CollapsibleContainer";
import dynamic from "next/dynamic";
import Head from "next/head";
import Link from "next/link";
import React, {
  Fragment,
  ReactElement,
  cloneElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  FaCamera,
  FaInfoCircle,
  FaLightbulb,
  FaList,
  FaMapMarkerAlt,
  FaQuestionCircle,
  FaStar,
  FaYoutube,
} from "react-icons/fa";
import {
  IoBagCheck,
  IoChatboxEllipses,
  IoFastFoodSharp,
} from "react-icons/io5";
import { MdDoNotDisturbAlt } from "react-icons/md";
import { RiBookmark3Fill } from "react-icons/ri";
import {
  isPhotographyProductId,
  isSumoPracticeProductId,
} from "../../../constants/products";
import {
  GetAllProductOptionsQuery,
  GetProductCollectionsQuery,
  GetProductQuery,
} from "../../../graphql/operations";
import useAvailability from "../../../hooks/useAvailability";
import Custom404 from "../../../pages/404";
import { getCityUrl } from "../../../utils/getCityUrl";
import useWindowSize from "../../../utils/useWindowSize";
import { useAnalyticsTracker } from "../../Atoms/Analytics";
import { useSession } from "../../Atoms/AppSession";
import Avatar from "../../Atoms/Avatar";
import BreadCrumbs from "../../Atoms/BreadCrumbs";
import { CloudinaryImageUrl } from "../../Atoms/Cloudinary";
import LazyInView from "../../Atoms/LazyInView";
import Price from "../../Atoms/Price";
import SkeletonCalendar from "../../Atoms/Skeleton/SkeletonCalendar";
import DietarySummaryCard, {
  DietarySummaryCardProps,
} from "../../Cards/DietarySummaryCard/DietarySummaryCard";
import ToggleSection from "../../Cards/ToggleSection";
import CancellationPolicyIncentive from "../Checkout/Parts/CancellationPolicyIncentive";
import SecurePaymentsFooter from "../Checkout/Parts/SecurePaymentsFooter";
import Announcement from "./Parts/Announcement";
import AnnouncementNew from "./Parts/AnnouncementNew";
import { CollapsibleAvailableGuides } from "./Parts/AvailableGuides";
import CharterReasons from "./Parts/CharterReasons";
import { CollapsibleProductContainer } from "./Parts/CollapsibleProductContainer";
import { CollapsibleProductContainerProps } from "./Parts/CollapsibleProductContainer/CollapsibleProductContainer";
import CollectionLinks from "./Parts/CollectionLinks";
import CreatorExcerpt, { CreatorExcerptProps } from "./Parts/CreatorExcerpt";
import FAQsSection, { FAQsSectionProps } from "./Parts/FAQsSection";
import GeneralInformation from "./Parts/GeneralInformation";
import Highlights from "./Parts/Highlights";
import Inclusions from "./Parts/Inclusions";
import Itinerary from "./Parts/Itinerary";
import KeyInformation from "./Parts/KeyInformation";
import MeetingPlace, { MeetingPlaceProps } from "./Parts/MeetingPlace";
import Omissions from "./Parts/Omissions";
import PickupPlace, { PickupPlaceProps } from "./Parts/PickupPlace";
import ProductCalendar from "./Parts/ProductCalendar";
import ProductMediaGallery from "./Parts/ProductMediaGallery";
import ProductMediaGalleryNew from "./Parts/ProductMediaGalleryNew";
import ProductNavigation from "./Parts/ProductNavigation";
import ProductNavigationNew from "./Parts/ProductNavigationNew";
import TAProductLogo from "./Parts/ProductTALogos";
import RecentTourPhotos from "./Parts/RecentTourPhotos";
import Stats from "./Parts/Stats";
import { BotBar, StickyBottomBar } from "./Parts/StickyBottomBar";
import Story from "./Parts/Story";
import TotalBookings from "./Parts/TotalBookings";
import YoutubeVideoPromotion from "./Parts/YoutubeVideoPromotion";
import {
  CALENDAR_AVAILABILITY_CACHE,
  OTHER_TOURS_IN_CITY_LIMIT,
  SIMILAR_PRODUCTS_LIMIT,
  scrollToCollapsibleContainer,
} from "./helpers";
import styles from "./Product.module.scss";
import {
  CollapsibleContainerName,
  collapsibleContainerConfigs,
} from "./collapsibleContainerAbTest";

const HurryUpMessage = dynamic(() => import("../../Cards/HurryUpMessage"), {
  ssr: false,
});

export type ProductTemplateProps = {
  data: {
    product: GetProductQuery["product"];
    productOptions: GetAllProductOptionsQuery["productOptions"]["records"];
    productCityCollections: GetProductCollectionsQuery["productCollections"]["records"];
    productCountryCollections: GetProductCollectionsQuery["productCollections"]["records"];
  };
};

const generateJsonLd = (product: GetProductQuery["product"]) => {
  if (!product) return;

  const { media, prices, reviewSummary, faqs } = product;
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Product",
      image: media && media.length > 0 ? media[0].url : "",
      name: product.translation.name,
      description: product.translation.metaDescription,
      priceRange: `${Number(prices[0].amount).toLocaleString("ja-JP", {
        style: "currency",
        currency: "USD",
      })} ~`,
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue:
          reviewSummary.average > 0 ? reviewSummary.average.toFixed(1) : 5,
        bestRating: 5,
        ratingCount:
          reviewSummary.numReviews > 0 ? reviewSummary.numReviews : 5,
      },
      reviews: [],
    },
    !!faqs && faqs.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((faq: { question: any; answer: any }) => {
            return {
              "@type": "Question",
              name: faq.question,
              acceptedAnswer: {
                "@type": "Answer",
                text: faq.answer,
              },
            };
          }),
        }
      : {},
  ];
  return JSON.stringify(jsonLd);
};

declare global {
  interface Window {
    google_optimize?: any;
    gtag: any;
  }
}

const ProductTemplate: React.FC<ProductTemplateProps> = ({ data }) => {
  const { width } = useWindowSize();
  const reviewsRef = useRef<CollapsibleContainerRef | HTMLDivElement>(null);
  const summaryRef = useRef<CollapsibleContainerRef | HTMLDivElement>(null);
  const highlightsRef = useRef<CollapsibleContainerRef | HTMLDivElement>(null);
  const locationRef = useRef<CollapsibleContainerRef | HTMLDivElement>(null);
  const faqsRef = useRef<CollapsibleContainerRef | HTMLDivElement>(null);
  const availabilityRef = useRef<HTMLDivElement>(null);
  const navigationRef = useRef<HTMLDivElement>(null);
  const botRef = useRef<HTMLDivElement>(null);
  const [shouldGalleryOpen, setGalleryOpen] = useState(false);
  const [inlineCalendar, setInlineCalendar] = useState(true);
  const [isAvailable, setIsAvailable] = useState(true);
  const [chatStyle, setChatStyle] = useState(false);
  const [isPrivate, setIsPrivate] = useState(false);
  const [isPageLoaded, setPageLoaded] = useState(false);
  const toggleIsPrivate = useCallback(
    () => setIsPrivate((isPrivate) => !isPrivate),
    [],
  );

  const { isMobile } = useWindowSize();

  const { product, productOptions } = data;

  const initialAvailability = useAvailability({
    productId: product.id,
    isPrivate,
    duration: { months: 3 },
    cache: CALENDAR_AVAILABILITY_CACHE,
  });

  const collapsibleContainerAbValue = useFeatureValue(
    "product-page-collapsible-containers",
    "none",
  );

  const navigationHeaderIsRemovedAb = useFeatureIsOn(
    "product-page-navigation-header",
  );

  const productPageRedesign = useFeatureValue(
    "product-page-media-gallery-redesign",
    "control",
  );
  const useProductPageRedesign = productPageRedesign === "redesign";
  const ProductMediaGalleryComponent = useProductPageRedesign
    ? ProductMediaGalleryNew
    : ProductMediaGallery;
  const ProductNavigationComponent = useProductPageRedesign
    ? ProductNavigationNew
    : ProductNavigation;
  const AnnouncementComponent = useProductPageRedesign
    ? AnnouncementNew
    : Announcement;

  const collapsibleAbConfig =
    collapsibleContainerConfigs[collapsibleContainerAbValue];
  const componentOrder = collapsibleAbConfig.order;
  const collapsibleComponents = collapsibleAbConfig.collapsed;
  const seperateItinerary = collapsibleAbConfig.separateItinerary;
  const renameYoutubeTitle = collapsibleAbConfig.renameYoutubeTitle;

  const tracker = useAnalyticsTracker();
  const jsonLd = useMemo(() => generateJsonLd(data.product), [data.product]);
  const session = useSession();
  const { countryCode } = session;

  const handleScroll = useCallback(() => {
    if (
      availabilityRef.current !== null &&
      window.scrollY > 1309 &&
      width > 839
    ) {
      setInlineCalendar(false);
    }
    if (
      availabilityRef.current !== null &&
      window.scrollY < 1309 &&
      width > 839
    ) {
      setInlineCalendar(true);
    }
    if (window.scrollY > -1 && width <= 839) {
      setChatStyle(true);
    } else {
      setChatStyle(false);
    }
  }, [width]);

  useEffect(() => {
    if (!product) return;

    if (typeof window !== "undefined") {
      if (typeof window.gtag === "function") {
        window.gtag("event", "view_item", {
          currency: product.prices[0].currency,
          value: product.prices[0].amount,
          items: [
            {
              item_id: product.id,
              item_name: product.translation.name,
              currency: product.prices[0].currency,
              index: 0,
              price: product.prices[0].amount,
              quantity: 1,
            },
          ],
        });
      }

      window.addEventListener("scroll", handleScroll, { passive: true });
      return () => {
        window.removeEventListener("scroll", handleScroll);
      };
    }
  }, [handleScroll, product]);

  const addToViewedItems = (id) => {
    let existingEntries = JSON.parse(localStorage.getItem("viewedItems"));
    if (existingEntries == null) existingEntries = [];
    let entry = id;
    if (!existingEntries.includes(entry)) {
      existingEntries.unshift(entry);
      existingEntries = existingEntries.slice(0, 5);
      localStorage.setItem("viewedItems", JSON.stringify(existingEntries));
    }
  };

  useEffect(() => {
    if (product) addToViewedItems(product.id);
    setPageLoaded(true);
  }, [product]);

  if (!data || !product) return <Custom404 />;

  const { id, media, translation, location } = product;
  const productBreadcrumbs = (
    <BreadCrumbs
      countryName={location.country.name}
      items={[
        {
          name: location.translation.name,
          pathname: `/tours-${location.country.name.toLocaleLowerCase()}/${getCityUrl(
            location,
          )}`,
        },
      ]}
      pageName={translation.name}
    />
  );

  const AvailabilityWidget = (
    <aside
      className={styles.LayoutSidebar}
      ref={availabilityRef}
      style={
        inlineCalendar
          ? {}
          : {
              position: "sticky",
              top: 60,
              border: "1px solid #e5e5e5",
              padding: 20,
              borderRadius: 6,
              boxShadow: "0px 1px 5px 0px #dbdbdb",
            }
      }
    >
      <div className={styles.BookingWidget}>
        {initialAvailability.loading ? (
          <SkeletonCalendar />
        ) : (
          <ProductCalendar
            countryName={product.location.country.name}
            inlineCalendar={inlineCalendar}
            productId={product.id}
            productName={product.translation.name}
            faqs={product.faqs}
            location={product.location}
            productOptions={productOptions}
            isPrivate={isPrivate}
            availabilityType={product.availabilityType}
            toggleIsPrivate={toggleIsPrivate}
            privatePrices={product.privatePrices}
            initalAvailability={initialAvailability}
            setIsAvailable={setIsAvailable}
          >
            <Price id={product.id} size="large" />
          </ProductCalendar>
        )}

        {product.cancellationLimit && (
          <CancellationPolicyIncentive
            limitInHours={product.cancellationLimit}
          />
        )}
        <SecurePaymentsFooter />
        {isSumoPracticeProductId(product.id) ? (
          <p className={styles.SumoAlternateMsg}>
            <b>
              Can&apos;t find available spots? <br />
              We have more Sumo Practice Tours
              <br />
              close to this tour
            </b>
            <br />
            <Link href="/sumo-japan">Find out more info here</Link>
          </p>
        ) : null}
      </div>
    </aside>
  );

  const moreToursLink = (
    <div
      style={{
        margin: "0 auto",
        textAlign: "center",
        marginTop: 20,
      }}
    >
      <Link
        href={`/tours-${location.country.name.toLowerCase()}/${getCityUrl(
          location,
        )}`}
        className={styles.ViewMoreProductsBtn}
      >
        View More Tours in {location.translation.name}
      </Link>
    </div>
  );

  const itinerary = translation.itinerary?.trim() || "";

  const productComponents = (
    [
      {
        key: "summary",
        component: (
          <CollapsibleProductContainer
            ref={summaryRef}
            label="Summary"
            Icon={RiBookmark3Fill}
            ga4Label="summary"
          >
            <Highlights data={translation.highlights?.trim() || ""} />
          </CollapsibleProductContainer>
        ),
      },
      {
        key: "endorsement",
        component: product.featuredGuide &&
          product.translation.featuredGuideComment && (
            <CollapsibleProductContainer
              label="Endorsement"
              Icon={IoChatboxEllipses}
              ga4Label="endorsement"
              fallbackRenderer={(content) =>
                cloneElement(content as ReactElement<CreatorExcerptProps>, {
                  canExpand: true,
                })
              }
            >
              <CreatorExcerpt data={data} />
            </CollapsibleProductContainer>
          ),
      },
      {
        key: "inclusions",
        component: product.inclusions && (
          <CollapsibleProductContainer
            label="What's Included"
            Icon={IoBagCheck}
            ga4Label="inclusions"
            fallbackRenderer={(content) => (
              <div
                style={{ display: "flex", flexDirection: "column", gap: 16 }}
              >
                <h5 className="DetailTitle">What&apos;s included</h5>
                {content}
              </div>
            )}
          >
            <Inclusions inclusions={product.inclusions} />
          </CollapsibleProductContainer>
        ),
      },
      {
        key: "guides",
        component: <CollapsibleAvailableGuides product={product} />,
      },
      {
        key: "omissions",
        component: translation.notIncluded &&
          translation.notIncluded.length > 0 && (
            <CollapsibleProductContainer
              label="Omissions"
              ga4Label="omissions"
              Icon={MdDoNotDisturbAlt}
            >
              <Omissions omissions={translation.notIncluded.split("\n")} />
            </CollapsibleProductContainer>
          ),
      },
      {
        key: "highlights",
        component: (
          <CollapsibleProductContainer
            ref={highlightsRef}
            label="Highlights"
            ga4Label="highlights"
            Icon={FaLightbulb}
          >
            <Story details={product.details} />
          </CollapsibleProductContainer>
        ),
      },
      {
        key: "recentPhotos",
        component: product.recentPhotos.length > 0 &&
          !isPhotographyProductId(product.id) && (
            <CollapsibleProductContainer
              label="Recent Photos"
              ga4Label="recentPhotos"
              Icon={FaCamera}
              fallbackRenderer={(content) => (
                <div>
                  <p
                    style={{
                      fontWeight: 700,
                      fontSize: 22.5,
                      marginBottom: 20,
                      marginTop: 20,
                    }}
                  >
                    Photos from the Recent Tours
                  </p>
                  {content}
                </div>
              )}
            >
              <RecentTourPhotos recentPhotos={product.recentPhotos} />
            </CollapsibleProductContainer>
          ),
      },
      {
        key: "meetingPlace",
        component: (
          <CollapsibleProductContainer
            ga4Label="meetingPlace"
            label={
              product.hasAccomodationVehiclePickup
                ? "Pickup Point"
                : "Meeting Point"
            }
            ref={locationRef}
            Icon={FaMapMarkerAlt}
            fallbackRenderer={(content) =>
              cloneElement(
                content as ReactElement<PickupPlaceProps | MeetingPlaceProps>,
                {
                  showLabel: true,
                },
              )
            }
          >
            {product.hasAccomodationVehiclePickup ? (
              <PickupPlace pickupLocationZone={product.pickupLocationZone} />
            ) : (
              <MeetingPlace
                name={translation.meetingPlaceName}
                description={translation.meetingPlaceDescription}
                googleMapsUrl={product.meetingPlaceGoogleMapsUrl}
                query={product.meetingPlaceGoogleMapsQuery}
                lazyMap
                withFrame
              />
            )}
          </CollapsibleProductContainer>
        ),
      },
      {
        key: "itinerary",
        component: (
          <CollapsibleProductContainer
            label={"Itinerary"}
            ga4Label="itinerary"
            Icon={FaList}
          >
            <Itinerary data={{ itinerary }} showHeader={false} />
          </CollapsibleProductContainer>
        ),
      },
      {
        key: "generalInfo",
        component: (
          <CollapsibleProductContainer
            label="General Information"
            ga4Label="generalInfo"
            Icon={FaInfoCircle}
          >
            <GeneralInformation
              data={{
                itinerary: seperateItinerary ? undefined : itinerary,
                cancellationPolicy: translation.cancellationPolicy
                  .trim()
                  .split("\n"),
                remarks: translation.remarks
                  ? translation.remarks.trim().split("\n")
                  : null,
              }}
            />
          </CollapsibleProductContainer>
        ),
      },
      {
        key: "dietaryInfo",
        component: product.dietaryRestrictions.length > 0 && (
          <CollapsibleProductContainer
            label="Dietary Information"
            ga4Label="dietaryInfo"
            Icon={IoFastFoodSharp}
            fallbackRenderer={(content) => (
              <ToggleSection title="Dietary Information">
                {cloneElement(
                  content as ReactElement<DietarySummaryCardProps>,
                  {
                    withBorder: true,
                    containerStyle: {
                      paddingTop: 16,
                    },
                    allergenStyle: {
                      paddingBottom: 16,
                    },
                  },
                )}
              </ToggleSection>
            )}
          >
            <DietarySummaryCard
              dietaryRestrictions={product.dietaryRestrictions}
            />
          </CollapsibleProductContainer>
        ),
      },
      {
        key: "faqs",
        component: product?.faqs && product.faqs.length > 0 && (
          <CollapsibleProductContainer
            label="FAQs"
            ga4Label="faqs"
            ref={faqsRef}
            Icon={FaQuestionCircle}
            fallbackRenderer={(content) => (
              <ToggleSection title={"FAQ of This Tour"}>
                {cloneElement(content as ReactElement<FAQsSectionProps>, {
                  addBottomBorder: true,
                  addTopBottomPadding: true,
                })}
              </ToggleSection>
            )}
          >
            <FAQsSection faqs={product.faqs ?? []} />
          </CollapsibleProductContainer>
        ),
      },
      {
        key: "reviews",
        component: (
          <CollapsibleProductContainer
            ref={reviewsRef}
            ga4Label="reviews"
            label="Reviews"
            Icon={FaStar}
          >
            <LazyInView
              fallback={<span>loading reviews</span>}
              loader={() => import("./Parts/ReviewsList")}
              render={(loaded) => {
                const ProductReviews = loaded.default;
                return <ProductReviews productId={id} tracker={tracker} />;
              }}
            />
          </CollapsibleProductContainer>
        ),
      },
      {
        key: "youtube",
        component: product.youtubes && product.youtubes.length > 0 && (
          <CollapsibleProductContainer
            label={renameYoutubeTitle ? "YouTube Video" : "Video"}
            Icon={FaYoutube}
            ga4Label="youtube"
          >
            <YoutubeVideoPromotion
              productId={product.id}
              youtube={product.youtubes[0]}
            />
          </CollapsibleProductContainer>
        ),
      },
    ] as {
      key: CollapsibleContainerName;
      component: ReactElement;
    }[]
  )
    .filter(({ key, component }) => componentOrder.includes(key) && !!component)
    .sort(
      (a, b) => componentOrder.indexOf(a.key) - componentOrder.indexOf(b.key),
    )
    .map(({ component, key }) => {
      const isCollapsible = collapsibleComponents.includes(key);
      const className = classNames(component.props.className, {
        [styles.Uncollapsible]: !isCollapsible,
      });

      return isCollapsible ? (
        React.cloneElement<CollapsibleProductContainerProps>(component, {
          key,
          abValue: collapsibleContainerAbValue,
          isCollapsible,
        })
      ) : (
        <div key={key} className={className}>
          {React.cloneElement(component, {
            isCollapsible,
          })}
        </div>
      );
    });

  return (
    <>
      <Head>
        {product.state === 4 && (
          <meta name="robots" content="noindex, nofollow" />
        )}
        <title>{product.translation.name}</title>
        <meta
          name="description"
          content={
            product.translation.metaDescription || product.translation.name
          }
        />
        {product.mainImage && (
          <meta
            name="thumbnail"
            content={CloudinaryImageUrl({
              src: product.mainImage,
              transformations: ["w_1200"],
            })}
          />
        )}
        <meta name="viewport" content="initial-scale=1.0, width=device-width" />
        <meta
          property="og:url"
          content={`https://www.magical-trip.com/product/${id}`}
        />
        <meta
          property="og:title"
          content={product.translation.ogTitle || product.translation.name}
        />
        <meta property="og:site_name" content={"MagicalTrip"} />
        <meta
          property="og:description"
          content={
            product.translation.ogDescription || product.translation.name
          }
        />
        <meta property="og:type" content="website" />

        <meta
          property="og:image"
          content={CloudinaryImageUrl({
            src: product.mainImage,
            transformations: ["w_1200"],
          })}
        />

        <meta property="og:image:width" content={String(1200)} />
        <meta property="og:image:height" content={String(630)} />
        <meta name="twitter:card" content="summary_large_image" />
        <script
          key="json-ld"
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: jsonLd }}
        />
        <link
          rel="canonical"
          href={`https://www.magical-trip.com/product/${product.id}`}
        />
      </Head>

      {useProductPageRedesign && (
        <div className="AppLayout">{productBreadcrumbs}</div>
      )}

      <ProductMediaGalleryComponent
        media={media}
        productId={product.id}
        productName={translation.name}
        shouldGalleryOpen={shouldGalleryOpen}
        setGalleryOpen={setGalleryOpen}
      />

      {!navigationHeaderIsRemovedAb && (
        <ProductNavigationComponent
          navigationRef={navigationRef}
          reviewsRef={reviewsRef}
          summaryRef={summaryRef}
          highlightsRef={highlightsRef}
          locationRef={locationRef}
          faqsRef={faqsRef}
          setGalleryOpen={setGalleryOpen}
          hasFAQ={product?.faqs && product.faqs.length > 0}
        />
      )}
      <AnnouncementComponent
        id={id}
        initialAnnouncement={product.announcement}
      />

      <div
        className="AppLayout"
        style={{
          overflow: "initial",
          marginTop: 30,
          marginBottom: 30,
        }}
      >
        <StickyBottomBar topOffset={0} stopEl={botRef}>
          <BotBar
            productId={id}
            name={translation.name}
            isAvailable={isAvailable}
            onSelectDates={() =>
              availabilityRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "center",
              })
            }
          />
        </StickyBottomBar>
        {chatStyle ? (
          <style>{`
          @media screen and (max-width: 839px) {
            #launcher {
              bottom: 80px !important;
            }
          } 
          @media screen and (max-width: 640px) {
         
            
            #launcher {
              left: 0px !important;
            }
            #launcher {
              right: auto !important;
            }
           
          }
          `}</style>
        ) : (
          <style>{`
          @media screen and (max-width: 839px) {
            #launcher {
              bottom: 0px !important;
            }
          } 
        @media screen and (max-width: 640px) {
       
          
          #launcher {
            left: auto !important;
          }
          #launcher {
            right: 0px !important;
          }
         
        }
        `}</style>
        )}

        {!useProductPageRedesign && productBreadcrumbs}

        <div className={styles.ProductColumns}>
          <div className={styles.ProductContent}>
            <h1 className={styles.ProductDetailTitle}>{translation.name}</h1>
            <div className={styles.ProductDetailGridContainer}>
              <div style={{ flexDirection: "column" }}>
                {translation.headline && (
                  <Fragment>
                    <span className={styles.ProductDetailSubtitle}>
                      {translation.headline}
                    </span>
                    {product.influencer && (
                      <div className={styles.InfluencerSubtitle}>
                        <Avatar src={product.influencer.iconSrc} />
                        <span>
                          {product.willInfluencerGuide
                            ? `Guided by `
                            : `Designed by `}
                          <b>{product.influencer.name}</b>
                        </span>
                      </div>
                    )}
                  </Fragment>
                )}
                <HurryUpMessage
                  isAvailableInRange={initialAvailability.isAvailableInRange}
                />

                <Stats
                  average={product.reviewSummary.average}
                  totalRecords={product.reviewSummary.numReviews}
                  onOpenReviews={() =>
                    scrollToCollapsibleContainer(
                      reviewsRef,
                      navigationRef,
                      isMobile,
                    )
                  }
                />
                <TotalBookings numPeople={product.metrics?.numPeople} />
              </div>
              <TAProductLogo productId={product.id} />
            </div>
            <KeyInformation
              duration={product.duration}
              isPrivate={product.availabilityType === "PRIVATE"}
              maximumInGroup={product.maximumInGroup}
              location={location}
              tags={product.tags}
            />
            {(product.hasAccomodationVehiclePickup ||
              product.hasDefaultVehiclePickup) && (
              <CharterReasons
                hasAccomodationVehiclePickup={
                  product.hasAccomodationVehiclePickup
                }
              />
            )}

            {width < 769 && isPageLoaded && AvailabilityWidget}
            <div className={styles.ProductComponents}>{productComponents}</div>
            <div ref={botRef} />
          </div>
          {isPageLoaded && width >= 769 && AvailabilityWidget}
        </div>
      </div>
      <div className="AppLayout">
        <LazyInView
          fallback={<span>loading available in next 2 days...</span>}
          loader={() => import("../Listing/CityAvailableProductsCarousel")}
          render={(loaded) => {
            const ProductsAvailableInTwo = loaded.default;

            // Keyed by city, so navigating to a product in another city doesn't keep
            // the previous city's products (keepPreviousData).
            return (
              <ProductsAvailableInTwo key={location.id} location={location} />
            );
          }}
        />

        {countryCode === "Asia/Tokyo" && moreToursLink}

        <LazyInView
          fallback={<span>loading similar products...</span>}
          loader={() => import("./Parts/SimilarProducts")}
          render={(loaded) => {
            const SimilarProducts = loaded.default;

            return (
              <SimilarProducts
                locationId={location.id}
                currentProduct={id}
                tags={product.tags.map((t) => t.id)}
                offset={0}
                limit={SIMILAR_PRODUCTS_LIMIT}
              />
            );
          }}
        />

        <LazyInView
          fallback={<span>loading similar products...</span>}
          loader={() => import("../../Cards/ItineraryCarousel")}
          render={(loaded) => {
            const ItineraryCarousel = loaded.default;

            return (
              <ItineraryCarousel
                locationSlug={location.slug}
                title={`Explore Popular Day Itineraries in ${location.translation.name}`}
              />
            );
          }}
        />

        <LazyInView
          fallback={<span>loading other tours...</span>}
          loader={() => import("./Parts/OtherToursInCity")}
          render={(loaded) => {
            const OtherToursInCity = loaded.default;

            return (
              <OtherToursInCity
                locationId={location.id}
                currentProduct={id}
                offset={0}
                limit={OTHER_TOURS_IN_CITY_LIMIT}
              />
            );
          }}
        />

        {countryCode !== "Asia/Tokyo" && moreToursLink}
        <CollectionLinks
          productCountryCollections={data.productCountryCollections}
          productCityCollections={data.productCityCollections}
          location={location}
        />
      </div>
    </>
  );
};

export default ProductTemplate;
